/**
 * BattleInfo 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { BattleSettlementData } from './battle_settlement_data'
import type { FileInfo } from './file_info'

/**
 * BattleInfo
 */
export interface BattleInfo {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped() */
  deleted_at?: string
  /** 删除原因 */
  deleted_reason?: string
  /** 禁用状态 */
  disabled?: boolean
  /** 禁用时间 */
  disabled_at?: string
  /** 禁用截止时间（null表示永久禁用） */
  disabled_until?: string
  /** 禁用原因 */
  disabled_reason?: string
  /** 创建管理员ID */
  creator_staff_id?: string
  /** 创建用户ID */
  creator_id?: string
  /** 红方战队名称 */
  red_name?: string
  /** 红方战队头像文件ID */
  red_avatar_id?: string
  /** 红方战队头像文件信息 */
  red_avatar?: FileInfo
  /** 蓝方战队名称 */
  blue_name?: string
  /** 蓝方战队头像文件ID */
  blue_avatar_id?: string
  /** 蓝方战队头像文件信息 */
  blue_avatar?: FileInfo
  /** 对线生命周期阶段（可选值：preparing=准备中; battling=对战中; settling=结算中; finished=已结束） */
  stage?: string
  /** 阶段变更时间 */
  stage_updated_at?: string
  /** 开始对线时间 */
  started_at?: string
  /** 首次开始时间 */
  first_started_at?: string
  /** 结算时间 */
  settled_at?: string
  /** 结束时间 */
  finished_at?: string
  /** 预期过期时间 */
  expire_at?: string
  /** 保护期结束时间 */
  protection_expire_at?: string
  /** 对线群胜负状态（可选值：undecided=未决定; red_win=红方胜; blue_win=蓝方胜; draw=平局） */
  victory_state?: string
  /** 对战时长（秒） */
  duration?: number
  /** 结算数据（对战结束时写入） */
  settlement?: BattleSettlementData
  /** 重启来源对线群ID */
  restart_from_id?: string
  /** 重启回合数 */
  restart_round?: number
  /** 对线群名称（格式：{red_name} vs {blue_name}） */
  name?: string
}

/**
 * BattleInfo Schema
 */
export const BattleInfoSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    deleted_at: {
      description:
        '删除时间（UTC），goframe会自动处理deleted_at，如果想避免goframe自动处理，请使用Unscoped()',
      format: '*time.Time',
      type: 'string',
      label: '删除时间',
    },
    deleted_reason: {
      description: '删除原因',
      format: 'string',
      type: 'string',
      label: '删除原因',
    },
    disabled: {
      description: '禁用状态',
      format: 'bool',
      type: 'boolean',
      label: '禁用状态',
    },
    disabled_at: {
      description: '禁用时间',
      format: '*time.Time',
      type: 'string',
      label: '禁用时间',
    },
    disabled_until: {
      description: '禁用截止时间（null表示永久禁用）',
      format: '*time.Time',
      type: 'string',
      label: '禁用截止时间',
    },
    disabled_reason: {
      description: '禁用原因',
      format: 'string',
      type: 'string',
      label: '禁用原因',
    },
    creator_staff_id: {
      description: '创建管理员ID',
      format: 'string',
      type: 'string',
      label: '创建管理员ID',
    },
    creator_id: {
      description: '创建用户ID',
      format: 'string',
      type: 'string',
      label: '创建用户ID',
    },
    red_name: {
      description: '红方战队名称',
      format: 'string',
      type: 'string',
      label: '红方战队名称',
    },
    red_avatar_id: {
      description: '红方战队头像文件ID',
      format: 'string',
      type: 'string',
      label: '红方战队头像文件ID',
    },
    red_avatar: {
      $ref: 'FileInfo',
      description: '红方战队头像文件信息',
      label: '红方战队头像文件信息',
    },
    blue_name: {
      description: '蓝方战队名称',
      format: 'string',
      type: 'string',
      label: '蓝方战队名称',
    },
    blue_avatar_id: {
      description: '蓝方战队头像文件ID',
      format: 'string',
      type: 'string',
      label: '蓝方战队头像文件ID',
    },
    blue_avatar: {
      $ref: 'FileInfo',
      description: '蓝方战队头像文件信息',
      label: '蓝方战队头像文件信息',
    },
    stage: {
      description:
        '对线生命周期阶段（可选值：preparing=准备中; battling=对战中; settling=结算中; finished=已结束）',
      format: 'cons.BattleStage',
      type: 'string',
      label: '对线生命周期阶段',
    },
    stage_updated_at: {
      description: '阶段变更时间',
      format: '*time.Time',
      type: 'string',
      label: '阶段变更时间',
    },
    started_at: {
      description: '开始对线时间',
      format: '*time.Time',
      type: 'string',
      label: '开始对线时间',
    },
    first_started_at: {
      description: '首次开始时间',
      format: '*time.Time',
      type: 'string',
      label: '首次开始时间',
    },
    settled_at: {
      description: '结算时间',
      format: '*time.Time',
      type: 'string',
      label: '结算时间',
    },
    finished_at: {
      description: '结束时间',
      format: '*time.Time',
      type: 'string',
      label: '结束时间',
    },
    expire_at: {
      description: '预期过期时间',
      format: '*time.Time',
      type: 'string',
      label: '预期过期时间',
    },
    protection_expire_at: {
      description: '保护期结束时间',
      format: '*time.Time',
      type: 'string',
      label: '保护期结束时间',
    },
    victory_state: {
      description:
        '对线群胜负状态（可选值：undecided=未决定; red_win=红方胜; blue_win=蓝方胜; draw=平局）',
      format: 'cons.BattleVictoryState',
      type: 'string',
      label: '对线群胜负状态',
    },
    duration: {
      description: '对战时长（秒）',
      format: 'int64',
      type: 'integer',
      label: '对战时长',
    },
    settlement: {
      $ref: 'BattleSettlementData',
      description: '结算数据（对战结束时写入）',
      label: '结算数据',
    },
    restart_from_id: {
      description: '重启来源对线群ID',
      format: 'string',
      type: 'string',
      label: '重启来源对线群ID',
    },
    restart_round: {
      description: '重启回合数',
      format: 'int',
      type: 'integer',
      label: '重启回合数',
    },
    name: {
      description: '对线群名称（格式：{red_name} vs {blue_name}）',
      format: 'string',
      type: 'string',
      label: '对线群名称',
    },
  },
  type: 'object',
}
